"use client";

import React, { useState, useRef } from "react";
import Image from "next/image";
import { getPhotosphereThumbnailUrl, getPhotosphereImageUrl } from "@/lib/utils/imageUrls";
import dynamic from "next/dynamic";
import PanelFFXIV from "@/app/components/PanelFFXIV";
import TextFFXIV from "@/app/components/TextFFXIV";
import PlayerAvatar, { calculateDuplicateNumbers } from "@/app/components/PlayerAvatar";
import { FaMagnifyingGlass } from "react-icons/fa6";
import GameModeConfigSeparator from "../GameModes/GameModeConfigSeparator";
import { calculateDistance, PlayerResultData } from "../GameResult/PlayerResultsList";
import { PRECISION_MIN_DISTANCE } from "@/lib/types/common/ScoringConstants";
import { ImCheckmark } from "react-icons/im";
import { FaSkull, FaEye } from "react-icons/fa6";
import { FaMapMarkerAlt } from "react-icons/fa";
import { IoClose } from "react-icons/io5";
import { useGame } from "@/app/providers/GameContextProvider";
import { useMainMap } from "@/app/providers/MainMapContextProvider";
import { useTranslation } from "@/hooks/useTranslation";
import { MdOutlineZoomIn } from "react-icons/md";
import { motion } from "framer-motion";
import { useGameQuality } from '@/app/providers/GameQualityProvider';
import { GameHazard, GameMode } from "@/lib/types/common/GameMode";
import { convertDistanceToYalms } from "@/lib/utils/coordinateConversion";
import { hazards } from "@/app/(front)/components/GamePlay/hazards";
import { PhotoSphereViewer, PhotoSphereViewerAPI } from "@/app/components/PhotoSphereViewer";
import { FaBiohazard } from "react-icons/fa6";
import HazardIcon from "../HazardIcon";
import { CoopRole, Player } from "@/lib/types/common/Player";
import { AUDIO, useAudio } from "@/app/providers/AudioProvider";
import { getTeamColorHex } from "@/lib/config/teams";
import { useIsMobile } from "@/hooks/useIsMobile";

const ResultRecapMap = dynamic(() => import("../GameResult/ResultRecapMap"), { ssr: false });

const RoundRecap = ({
    selectedRoundIndex,
    setSelectedRoundIndex,
    setHideOverlay
}: {
    selectedRoundIndex: number | null;
    setSelectedRoundIndex: React.Dispatch<React.SetStateAction<number | null>>;
    setHideOverlay: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
    const { gameState, room } = useGame();
    const { allMaps } = useMainMap();
    const { getBackdropBlurIntensity } = useGameQuality();
    const { t, locale } = useTranslation();
    const isMobile = useIsMobile();
    const { play } = useAudio();
    const [hoveredMapPlayer, setHoveredMapPlayer] = useState<string | null>(null);
    const [lockedMapPlayer, setLockedMapPlayer] = useState<string | null>(null);
    const [showHazards, setShowHazards] = useState<boolean>(true);
    const viewerRef = useRef<PhotoSphereViewerAPI | null>(null);

    // Reset showed hazards when round changes
    React.useEffect(() => {
        setShowHazards(true);
    }, [selectedRoundIndex]);


    if (!gameState || !room) return null;

    return (
        <PanelFFXIV className={`border-2 border-x-[#c0a270] border-y-[#e0c290] flex flex-col !pt-2.5 !pb-0 ${isMobile ? '!px-3 !w-full !max-w-[95vw]' : '!px-6 !w-[1200px] !max-w-[1200px] 2xl:!w-[1400px] 2xl:!max-w-[1400px]'}`}>
            <TextFFXIV className="text-lg font-medium mb-1 text-center" text={t("play.recap.roundRecaps")} />
            <GameModeConfigSeparator />
            <motion.div
                className={`grid ${isMobile ? 'grid-cols-2 gap-3' : 'grid-cols-5 gap-6'} my-4 md:my-6`}
                initial="hidden"
                animate="show"
                variants={{
                    hidden: {},
                    show: {
                        transition: {
                            staggerChildren: 0.2,
                            delayChildren: 0.4
                        }
                    }
                }}
            >
                {Array.from(gameState.roundHistory).map((roundResult, index) => (
                    <motion.div
                        key={`round-thumb-${roundResult.roundNumber}`}
                        className={`relative cursor-pointer group`}
                        onMouseEnter={() => { play(AUDIO.HOVER) }}
                        onClick={() => { play(AUDIO.CLICK); setSelectedRoundIndex(selectedRoundIndex === index ? null : index) }}
                        variants={{
                            hidden: { opacity: 0, y: -20 },
                            show: { opacity: 1, y: 0 }
                        }}
                    >
                        <div className={`relative grow ${isMobile ? 'h-[80px]' : 'h-[140px]'} rounded-lg overflow-hidden shadow-[0_0_5px_3px_rgba(0,0,0,0.3)] bg-gray-800 ${selectedRoundIndex === index ? 'border-2 border-x-[#c0a270] border-y-[#e0c290]' : 'border-2 border-[#444] hover:border-[#666]'}`}>
                            <img
                                src={getPhotosphereThumbnailUrl(roundResult.photosphere.id)}
                                alt={`Round ${roundResult.roundNumber} - ${roundResult.photosphere.mapName}`}
                                width={200}
                                height={120}
                                className="object-cover w-full h-full group-hover:brightness-[1.1] transition-all duration-100 brightness-[0.7] saturate-[0.5] group-hover:saturate-[1.1]"
                            />
                            {selectedRoundIndex === index && (
                                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                                    <div className={`flex items-center justify-center rounded-full ${getBackdropBlurIntensity('sm')} ${isMobile ? 'h-[50px] w-[50px]' : 'h-[80px] w-[80px]'} bg-black/50`}>
                                        <FaMagnifyingGlass className={`${isMobile ? 'text-3xl' : 'text-5xl'} text-[#c0a270]`} />
                                    </div>
                                </div>
                            )}
                            {selectedRoundIndex !== index && (
                                <div className={`absolute bottom-0 w-full bg-black/60 text-white text-center flex justify-center py-1 ${isMobile ? 'text-xs' : 'text'} font-contrax`}>
                                    {(() => {
                                        // Use roundPlayer from round history instead of live currentPlayer
                                        let roundPlayer: Player | undefined;
                                        let roundScore;
                                        if (gameState.isEffectivelyCoop) {
                                            // In Coop mode, show the total team score for the round
                                            const teamId = gameState.players?.get(room.sessionId)?.teamId;
                                            roundPlayer = roundResult.playerResults.get(room.sessionId);
                                            const teammate = Array.from(roundResult.playerResults.values()).find(p => p.teamId === teamId && p !== roundPlayer);
                                            const roundScoreSelf = roundPlayer?.scores[roundResult.roundNumber - 1] || 0;
                                            const roundScoreTeammate = teammate ? (teammate.scores[roundResult.roundNumber - 1] || 0) : 0;
                                            roundScore = roundScoreSelf + roundScoreTeammate;
                                        } else {
                                            roundPlayer = roundResult.playerResults.get(room.sessionId);
                                            roundScore = roundPlayer?.scores[roundResult.roundNumber - 1] || 0;
                                        }


                                        if (gameState.isEffectivelyEndless) {
                                            if (roundPlayer?.isEliminated) {
                                                return (<FaSkull className="text-red-400" />)
                                            }
                                            else {
                                                return (<ImCheckmark className="text-emerald-500" />)
                                            }
                                        }
                                        if (gameState.isEffectivelyFight) {
                                            const wasTeamEliminatedThisRound = roundResult.teamSnapshots?.some(snapshot => snapshot.teamId === roundPlayer?.teamId && snapshot.wasEliminatedThisRound);
                                            if (roundPlayer?.isEliminated || wasTeamEliminatedThisRound) {
                                                return (
                                                    <div className="flex gap-2 items-center">
                                                        {wasTeamEliminatedThisRound ? roundScore : null}
                                                        <FaSkull className="text-red-400" />
                                                    </div>
                                                )
                                            }
                                            else {
                                                return roundScore;
                                            }
                                        }
                                        if (gameState.isEffectivelyCoop) {
                                            const coopRole = roundPlayer?.coopRole as CoopRole;
                                            if (coopRole === CoopRole.PHOTOSPHERE) {
                                                return (
                                                    <div className="flex gap-2 items-center">
                                                        {roundScore}
                                                        <FaEye className="text-purple-400" />
                                                    </div>
                                                );

                                            } else if (coopRole === CoopRole.MAP) {
                                                return (
                                                    <div className="flex gap-2 items-center">
                                                        {roundScore}
                                                        <FaMapMarkerAlt className="text-blue-400" />
                                                    </div>
                                                );
                                            }
                                        }
                                        return roundScore;
                                    })()}
                                </div>
                            )}
                            <div className={`absolute top-1 left-1 bg-black/60 text-white ${isMobile ? 'px-1.5 py-0.5 text-xs' : 'px-2 py-0.5'} rounded font-contrax flex items-center gap-0.5`}>
                                <span className="mr-0.5">{roundResult.roundNumber}</span>
                                {roundResult.hazards.map((hazardName, idx) => {
                                    const hazard = hazards.find(h => h.name === hazardName.toLowerCase());
                                    if (!hazard) return null;
                                    return <HazardIcon key={`hazard-icon-${idx}`} hazard={hazard} size={isMobile ? 14 : 20} tooltip={false} className="translate-y-[-2px]" />;
                                })}
                            </div>
                        </div>
                    </motion.div>
                ))}
            </motion.div>

            {selectedRoundIndex !== null && (() => {
                const effectiveIndex = selectedRoundIndex ?? 0;
                const roundResult = Array.from(gameState.roundHistory)[effectiveIndex];

                // Create a map of team snapshots for this round (for Fight mode)
                const teamSnapshotMap = new Map<string, { color: string; isEliminated: boolean; wasEliminatedThisRound: boolean }>();
                if (roundResult.teamSnapshots) {
                    for (const snapshot of Array.from(roundResult.teamSnapshots)) {
                        teamSnapshotMap.set(snapshot.teamId, {
                            color: snapshot.color,
                            isEliminated: snapshot.isEliminated,
                            wasEliminatedThisRound: snapshot.wasEliminatedThisRound
                        });
                    }
                }

                // Helper to get team color for a player
                const getPlayerTeamColor = (teamId?: string): string | undefined => {
                    if (!teamId || (!gameState.isEffectivelyFight && !gameState.isEffectivelyCoop)) return undefined;
                    const snapshot = teamSnapshotMap.get(teamId);
                    if (snapshot) return getTeamColorHex(snapshot.color);
                    // Fallback to teamRankings snapshot (preserves color even when teams change)
                    const teamRanking = gameState.teamRankings?.find(t => t.teamId === teamId);
                    if (teamRanking) return getTeamColorHex(teamRanking.color);
                    // Last fallback to current team state
                    const team = gameState.teams.find(t => t.id === teamId);
                    return team ? getTeamColorHex(team.color) : undefined;
                };

                // Helper to check if a player's team was eliminated this round
                const wasPlayerTeamEliminatedThisRound = (teamId?: string): boolean => {
                    if (!teamId) return false;
                    const snapshot = teamSnapshotMap.get(teamId);
                    return snapshot?.wasEliminatedThisRound ?? false;
                };

                let playerResults: PlayerResultData[] = Array.from(roundResult.playerResults.entries()).map(([sessionId, player]) => {
                    const guess = player.guesses.get(roundResult.roundNumber.toString());
                    const roundScore = player.scores[roundResult.roundNumber - 1] || 0;
                    const gotPerfectScore = roundScore === 1000;
                    // Use teamId from player result (stored in round history) with fallback to current state
                    const teamId = player.teamId || gameState.players?.get(sessionId)?.teamId;

                    if (!guess) {
                        return {
                            sessionId,
                            name: player.name,
                            guessedOnCorrectMap: false,
                            roundScore: roundScore,
                            totalScore: player.totalScore,
                            hasGuessed: false,
                            avatarId: player.avatarId,
                            playerNumber: player.playerNumber || 0,
                            currentStreak: player.currentStreak || 0,
                            isEliminated: player.isEliminated || false,
                            gotPerfectScore: false,
                            teamId,
                        };
                    }
                    const guessedOnCorrectMap = guess.mapId === roundResult.photosphere.mapId;
                    let distance: number | undefined;
                    let guessedMapName: string | undefined;

                    if (guessedOnCorrectMap) {
                        distance = calculateDistance(
                            guess.x,
                            guess.y,
                            roundResult.photosphere.coord.x,
                            roundResult.photosphere.coord.y
                        ) - PRECISION_MIN_DISTANCE;
                    } else {
                        const guessedMap = allMaps.find(map => map.id === guess.mapId);
                        guessedMapName = guessedMap ? guessedMap.name[locale as keyof typeof guessedMap.name] : undefined;
                    }

                    return {
                        sessionId,
                        name: player.name,
                        guessedOnCorrectMap,
                        distance,
                        guessedMapName,
                        guessedMapId: guess.mapId,
                        roundScore,
                        totalScore: player.totalScore,
                        hasGuessed: true,
                        avatarId: player.avatarId,
                        playerNumber: player.playerNumber || 0,
                        currentStreak: player.currentStreak || 0,
                        isEliminated: player.isEliminated || false,
                        gotPerfectScore,
                        teamId,
                    };
                })

                if (gameState.isEffectivelyCoop) {
                    // Group by team
                    const teamGroups = new Map<string, PlayerResultData[]>();
                    playerResults.forEach(player => {
                        const teamId = player.teamId || 'no-team';
                        if (!teamGroups.has(teamId)) teamGroups.set(teamId, []);
                        teamGroups.get(teamId)!.push(player);
                    });
                    // Sort teams by max roundScore descending
                    const sortedTeams = Array.from(teamGroups.entries()).sort(([, a], [, b]) => {
                        const maxA = Math.max(...a.map(p => p.roundScore));
                        const maxB = Math.max(...b.map(p => p.roundScore));
                        return maxB - maxA;
                    });
                    // Sort within each team by roundScore descending
                    sortedTeams.forEach(([, players]) => {
                        players.sort((a, b) => b.roundScore - a.roundScore);
                    });
                    // Flatten
                    playerResults = sortedTeams.flatMap(([, players]) => players);
                } else {
                    playerResults.sort((a, b) => b.isEliminated ? -1 : a.isEliminated ? 1 : b.roundScore - a.roundScore);
                }

                // Calculate duplicate numbers for avatars
                const duplicateNumbers = calculateDuplicateNumbers(playerResults.map(p => ({ sessionId: p.sessionId, avatarId: p.avatarId })));

                return (
                    <div
                        key={`round-details`}
                        className="overflow-hidden"
                    >
                        <GameModeConfigSeparator />
                        <div
                            key={selectedRoundIndex}
                            className={`flex ${isMobile ? 'flex-col' : 'flex-row'} mt-4 md:mt-6 gap-4 md:gap-8 mb-5`}
                        >
                            <div className="flex flex-col grow px-2">
                                <div className={`text-shadow-border shadow-black mb-3 ${isMobile ? 'text-base' : 'text-xl'} text-center flex gap-2 justify-center items-center flex-wrap`}>
                                    <span>{t("play.roundNumber", { roundNumber: effectiveIndex + 1 })} - {roundResult.photosphere.mapName}</span>
                                    <div
                                        className="flex items-center h-[30px] gap-1"
                                    >
                                        {roundResult.hazards.map((hazardName, index) => {
                                            const hazard = hazards.find(h => h.name === hazardName.toLowerCase());
                                            if (!hazard) return null;
                                            return <HazardIcon key={`hazard-icon-${index}`} hazard={hazard} size={isMobile ? 20 : 25} />;
                                        })}
                                    </div>
                                </div>
                                <div className={`flex ${isMobile ? 'flex-col gap-4' : 'flex-row justify-between gap-5'}`}>
                                    {/* Mobile: Player results in a compact card format */}
                                    {isMobile ? (
                                        <div className="flex flex-col gap-2">
                                            {playerResults.map((player) => {
                                                const teamColor = getPlayerTeamColor(player.teamId);
                                                const wasTeamEliminatedThisRound = wasPlayerTeamEliminatedThisRound(player.teamId);
                                                const showEliminatedStyle = player.isEliminated && !wasTeamEliminatedThisRound;
                                                const isCoopMode = gameState.isEffectivelyCoop;
                                                const roundPlayerData = roundResult.playerResults.get(player.sessionId);
                                                const coopRole = roundPlayerData?.coopRole as CoopRole;
                                                const isPhotosphereRole = isCoopMode && coopRole === CoopRole.PHOTOSPHERE;

                                                return (
                                                    <div
                                                        key={`recap-player-mobile-${player.sessionId}`}
                                                        className={`flex items-center justify-between gap-2 p-2 bg-black/20 rounded ${showEliminatedStyle ? 'opacity-70 grayscale' : ''} ${gameState.isEffectivelyFight && wasTeamEliminatedThisRound ? 'line-through' : ''}`}
                                                    >
                                                        <div className="flex items-center gap-2">
                                                            <div className="w-6 h-6 relative flex-shrink-0">
                                                                <PlayerAvatar
                                                                    avatarId={player.avatarId}
                                                                    duplicateNumber={duplicateNumbers.get(player.sessionId) || 0}
                                                                    size={24}
                                                                    className="w-full h-full"
                                                                />
                                                            </div>
                                                            {isCoopMode && coopRole && coopRole !== CoopRole.NONE && (
                                                                <div className="w-4 h-4 flex items-center justify-center">
                                                                    {coopRole === CoopRole.PHOTOSPHERE ? (
                                                                        <FaEye className="text-purple-400 text-xs" />
                                                                    ) : (
                                                                        <FaMapMarkerAlt className="text-blue-400 text-xs" />
                                                                    )}
                                                                </div>
                                                            )}
                                                            <TextFFXIV
                                                                text={player.name}
                                                                className="truncate max-w-[80px] text-sm"
                                                                style={teamColor ? { color: teamColor } : undefined}
                                                            />
                                                        </div>
                                                        <div className="flex items-center gap-2 text-sm">
                                                            {!isPhotosphereRole && (
                                                                <span className="text-shadow-border shadow-black">
                                                                    {gameState.isEffectivelyEndless ? (
                                                                        player.isEliminated ? (
                                                                            <IoClose className="text-red-400" />
                                                                        ) : (
                                                                            <ImCheckmark className="text-green-400" />
                                                                        )
                                                                    ) : (
                                                                        player.roundScore
                                                                    )}
                                                                </span>
                                                            )}
                                                            {gameState.isEffectivelyFight && wasTeamEliminatedThisRound && (
                                                                <FaSkull className="text-red-400" />
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        /* Desktop: Original multi-column layout */
                                        <>
                                            <div className="flex flex-col gap-2">
                                                {/* Player name and avatar column */}
                                                {playerResults.map((player) => {
                                                    const teamColor = getPlayerTeamColor(player.teamId);
                                                    const wasTeamEliminatedThisRound = wasPlayerTeamEliminatedThisRound(player.teamId);
                                                    const showEliminatedStyle = player.isEliminated && !wasTeamEliminatedThisRound;
                                                    const isCoopMode = gameState.isEffectivelyCoop;
                                                    // Get coopRole from the round's playerResults snapshot
                                                    const roundPlayerData = roundResult.playerResults.get(player.sessionId);
                                                    const coopRole = roundPlayerData?.coopRole as CoopRole;

                                                    return (
                                                        <div
                                                            key={`recap-player-${player.sessionId}`}
                                                            className={`flex gap-2 items-center h-[30px] flex-1 ${showEliminatedStyle ? 'opacity-70 grayscale' : ''} ${gameState.isEffectivelyFight && wasTeamEliminatedThisRound ? 'line-through' : ''}`}
                                                        >
                                                            <div className="w-[30px] h-[30px] relative">
                                                                <PlayerAvatar
                                                                    avatarId={player.avatarId}
                                                                    duplicateNumber={duplicateNumbers.get(player.sessionId) || 0}
                                                                    size={30}
                                                                    className="w-full h-full"
                                                                    isLeader={gameState?.gameMasterId === player.sessionId}
                                                                    leaderIconSize={16}
                                                                />
                                                            </div>
                                                            {/* Coop role icon */}
                                                            {isCoopMode && coopRole && coopRole !== CoopRole.NONE && (
                                                                <div className="w-5 h-5 flex items-center justify-center" title={coopRole === CoopRole.PHOTOSPHERE ? t("game.coop.rolePhotosphere") : t("game.coop.roleMap")}>
                                                                    {coopRole === CoopRole.PHOTOSPHERE ? (
                                                                        <FaEye className="text-purple-400 text-sm" />
                                                                    ) : (
                                                                        <FaMapMarkerAlt className="text-blue-400 text-sm" />
                                                                    )}
                                                                </div>
                                                            )}
                                                            <TextFFXIV
                                                                text={player.name}
                                                                className="truncate max-w-[170px] !px-1"
                                                                style={teamColor ? { color: teamColor } : undefined}
                                                            />
                                                            {gameState.isEffectivelyFight && wasTeamEliminatedThisRound && (
                                                                <FaSkull className="text-red-400 ml-1" title={t("play.fight.eliminatedThisRound")} />
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                            {/* Guess column */}
                                            <div className="flex flex-col gap-2">
                                                {playerResults.map((player) => {
                                                    const wasTeamEliminatedThisRound = wasPlayerTeamEliminatedThisRound(player.teamId);
                                                    const showEliminatedStyle = player.isEliminated && !wasTeamEliminatedThisRound;
                                                    const isCoopMode = gameState.isEffectivelyCoop;
                                                    // Get coopRole from the round's playerResults snapshot
                                                    const roundPlayerData = roundResult.playerResults.get(player.sessionId);
                                                    const coopRole = roundPlayerData?.coopRole as CoopRole;
                                                    const isPhotosphereRole = isCoopMode && coopRole === CoopRole.PHOTOSPHERE;

                                                    return (
                                                        <div
                                                            key={player.sessionId}
                                                            className={`flex items-center h-[30px] relative text-shadow-border shadow-black ${showEliminatedStyle || (isCoopMode && isPhotosphereRole) ? 'opacity-0' : ''}`}
                                                        >
                                                            {player.hasGuessed && player.guessedOnCorrectMap && (
                                                                player.roundScore === 1000
                                                                    ? t("play.onTheSpot")
                                                                    : `${t("play.yalmsAway", { distance: convertDistanceToYalms(player.distance ?? 0) })}`
                                                            )}
                                                            {/* Hide "No guess" for photosphere players in Coop mode */}
                                                            {!player.hasGuessed && !isPhotosphereRole && <TextFFXIV text={t("play.noGuess")} className="text-gray-400" />}
                                                            {player.hasGuessed && !player.guessedOnCorrectMap && (
                                                                <div className="flex gap-2 items-center">
                                                                    <span className="truncate max-w-[150px]">{player.guessedMapName}</span>
                                                                    <div
                                                                        onMouseEnter={() => {
                                                                            play(AUDIO.HOVER);
                                                                            if (!lockedMapPlayer) {
                                                                                setHoveredMapPlayer(player.sessionId);
                                                                            }
                                                                        }}
                                                                        onMouseLeave={() => setHoveredMapPlayer(null)}
                                                                        onClick={() => {
                                                                            play(AUDIO.CLICK);
                                                                            // If clicking on the same locked player, unlock
                                                                            if (lockedMapPlayer === player.sessionId) {
                                                                                setLockedMapPlayer(null);
                                                                            } else {
                                                                                // Lock to this player
                                                                                setLockedMapPlayer(player.sessionId);
                                                                            }
                                                                        }}
                                                                        className={`cursor-pointer flex items-center transition-all w-[25px] h-[25px] duration-200 ${lockedMapPlayer === player.sessionId
                                                                            ? 'scale-110 drop-shadow-[0_0_3px_rgba(255,255,255,0.8)]'
                                                                            : 'hover:scale-105'
                                                                            }`}
                                                                    >
                                                                        <Image
                                                                            src="/map_icon.png"
                                                                            alt="View Map"
                                                                            width={25}
                                                                            height={25}
                                                                            className={`${lockedMapPlayer === player.sessionId
                                                                                ? 'brightness-125'
                                                                                : ''
                                                                                } saturate-[0]`}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                            {/* Score column */}
                                            <div className="flex flex-col gap-2">
                                                {playerResults.map((player) => {

                                                    if (gameState.isEffectivelyEndless) {
                                                        // For Endless mode, show status
                                                        const roundPlayer = roundResult.playerResults.get(player.sessionId);
                                                        const isEliminated = roundPlayer?.isEliminated || false;

                                                        return (
                                                            <div
                                                                key={player.sessionId}
                                                                className="flex items-center justify-end h-[30px] relative text-shadow-border shadow-black gap-1"
                                                            >
                                                                {isEliminated ? (
                                                                    <>
                                                                        <IoClose className="text-red-400 text-lg" />
                                                                        <span className="text-red-400 text-sm">{t("play.endless.eliminated")}</span>
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <ImCheckmark className="text-green-400 text-lg" />
                                                                    </>
                                                                )}
                                                            </div>
                                                        );
                                                    }

                                                    // For Coop mode, don't show score for photosphere player
                                                    const isCoopMode = gameState.isEffectivelyCoop;
                                                    // Get coopRole from the round's playerResults snapshot
                                                    const roundPlayerData = roundResult.playerResults.get(player.sessionId);
                                                    const coopRole = roundPlayerData?.coopRole as CoopRole;
                                                    const isPhotosphereRole = isCoopMode && coopRole === CoopRole.PHOTOSPHERE;

                                                    const wasTeamEliminatedThisRound = wasPlayerTeamEliminatedThisRound(player.teamId);
                                                    const showEliminatedStyle = player.isEliminated && !wasTeamEliminatedThisRound;
                                                    return (
                                                        <div
                                                            key={player.sessionId}
                                                            className={`flex items-center justify-end h-[30px] relative text-shadow-border shadow-black ${showEliminatedStyle ? 'opacity-0' : ''}`}
                                                        >
                                                            {/* Don't show score for photosphere role in Coop mode */}
                                                            {!isPhotosphereRole && <span>{player.roundScore}</span>}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                            {/* Map and Photosphere viewers - responsive layout */}
                            <div className={`flex ${isMobile ? 'flex-col gap-4' : 'flex-row gap-8'}`}>
                                <div className={`${isMobile ? 'h-[200px] w-full' : 'h-[300px] aspect-square'}`}>
                                    <ResultRecapMap
                                        key={`recap-map-${effectiveIndex}-${roundResult.photosphere.id}`}
                                        roundResult={roundResult}
                                        hoveredMapPlayer={lockedMapPlayer || hoveredMapPlayer}
                                        isOverlayVisible={true}
                                        height={isMobile ? "200px" : "300px"}
                                        showMouseTracker={false}
                                        showMapName={false}
                                        animateBoundsFit={false}
                                        zoom={0.1}
                                        minZoom={0.1}
                                        shouldFitBounds={lockedMapPlayer || hoveredMapPlayer || !playerResults.some(player => player.guessedOnCorrectMap) ? false : true}
                                    />
                                </div>
                                {/* Photosphere viewer */}
                                <div className={`relative rounded-lg overflow-hidden shadow-[0_0_10px_4px_rgba(0,0,0,0.5)] border-2 border-x-[#c0a270] border-y-[#e0c290] ${isMobile ? 'h-[200px] w-full' : 'h-[300px] w-[500px] min-w-[500px]'}`}>
                                    <PhotoSphereViewer
                                        ref={viewerRef}
                                        key={`photosphere-viewer-round-${selectedRoundIndex}-light`}
                                        src={getPhotosphereImageUrl(Array.from(gameState.roundHistory)[effectiveIndex].photosphere.id, "panorama_light.webp")}
                                        height={isMobile ? "200px" : "300px"}
                                        width={isMobile ? "100%" : "500px"}
                                        defaultYaw={-Math.PI / 2}
                                        hazards={roundResult.hazards as unknown as GameHazard[] || []}
                                        showHazards={showHazards}
                                        shader={(roundResult.hazards as unknown as GameHazard[])?.includes(GameHazard.PIXELATE) ? 'pixelate' : null}
                                        shaderSettings={{ pixelSize: 8 }}
                                    />
                                    <div className="absolute w-full h-full inset-0 shadow-[inset_0_0_8px_4px_rgba(0,0,0,0.4)] pointer-events-none"></div>
                                    <div className="absolute bottom-2 right-2 z-10 flex gap-2">
                                        {(() => {
                                            // Use snapshot if available (preserves settings even after going back to lobby)
                                            const snapshotGameMode = gameState.gameSettingsSnapshot?.gameMode ?? gameState.gameMode;
                                            const snapshotHasHazards = gameState.gameSettingsSnapshot?.hasHazards ?? gameState.gameSettings?.hasHazards;
                                            const showHazardsButton = snapshotGameMode === GameMode.HAZARD ||
                                                (snapshotGameMode === GameMode.CUSTOM && snapshotHasHazards);
                                            return showHazardsButton && (
                                                <div
                                                    onMouseEnter={() => play(AUDIO.HOVER)}
                                                    onClick={() => { play(AUDIO.CLICK); setShowHazards(!showHazards) }}
                                                    className={`border border-white/40 rounded-lg ${isMobile ? 'w-8 h-8' : 'w-10 h-10'} bg-black/60 hover:bg-[#c0a270]/50 hover:scale-110 flex items-center justify-center cursor-pointer`}
                                                >
                                                    <FaBiohazard className={`${isMobile ? 'text-2xl' : 'text-3xl'} ${showHazards ? 'text-white' : 'text-white/40'}`} />
                                                </div>
                                            );
                                        })()}
                                        <div
                                            onMouseEnter={() => play(AUDIO.HOVER)}
                                            onClick={() => { play(AUDIO.CLICK); setHideOverlay(true) }}
                                            className={`border border-white/40 rounded-lg ${isMobile ? 'w-8 h-8' : 'w-10 h-10'} bg-black/60 hover:bg-[#c0a270]/50 hover:scale-110 flex items-center justify-center cursor-pointer`}
                                        >
                                            <MdOutlineZoomIn className={`text-white ${isMobile ? 'text-2xl' : 'text-3xl'}`} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })()}
        </PanelFFXIV>
    );
};

export default RoundRecap;